import { InlineSearchItem, InlineSearchResult } from '../types';
import { highlightMatch } from './searchUtils';

const SEARCH_DEBOUNCE_MS = 300;

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	return (...args: Parameters<T>) => {
		if (timeoutId != null) clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn(...args), ms);
	};
}

export class SearchInitializer {
	/** We use this to interrupt a running fetch request */
	private controller: AbortController | null = null;

	private searchMenu: HTMLDivElement | null = document.querySelector('.search');

	init = () => {
		// focus the search field when the search icon is clicked
		document.querySelectorAll('.search > .search-icon').forEach((icon) => {
			icon.addEventListener('click', () => {
				const searchField = <HTMLInputElement>icon.closest('.search')?.querySelector('input.tx-solr-q');
				if (searchField) {
					searchField.focus();
				}
			});
		});

		if (this.searchMenu == null) {
			return;
		}

		this.searchMenu.addEventListener('mouseenter', () => {
			this.setMenuHeight(true);
		});

		const searchMenuInput = this.searchMenu?.querySelector<HTMLInputElement>('input.tx-solr-q');

		this.searchMenu.addEventListener('mouseleave', () => {
			if (searchMenuInput != document.activeElement) {
				this.setMenuHeight(false);
			} else {
				if (document.querySelector('.desktop-menu:hover') != null) {
					this.setMenuHeight(false);
				}
			}
		});

		document.body.addEventListener('click', () => {
			if (!this.searchMenu?.matches(':hover')) this.setMenuHeight(false);
		});

		this.handleInputFocus();

		const searchFieldsHeader: NodeListOf<HTMLInputElement> = document.querySelectorAll('header .tx-solr-q');
		const debouncedSendHeader = debounce((field: HTMLInputElement) => this.send(field, true), SEARCH_DEBOUNCE_MS);
		searchFieldsHeader.forEach((searchField) => {
			searchField.addEventListener('keyup', () => debouncedSendHeader(searchField));
		});

		const searchFieldsMain: NodeListOf<HTMLInputElement> = document.querySelectorAll('main .tx-solr-q');
		const debouncedSendMain = debounce((field: HTMLInputElement) => this.send(field, false), SEARCH_DEBOUNCE_MS);
		searchFieldsMain.forEach((searchField) => {
			searchField.addEventListener('keyup', () => debouncedSendMain(searchField));
		});

		const closeButton: NodeListOf<HTMLInputElement> = document.querySelectorAll('main .search-btn-close');
		closeButton.forEach((button) => {
			button.addEventListener('click', (event) => {
				const searchFormWrapper = button.closest('.tx-solr-search-form');
				const searchField = searchFormWrapper?.querySelector('input.tx-solr-q');
				if (searchField instanceof HTMLInputElement && searchFormWrapper != null) {
					searchField.value = '';
					const searchResultBoxes = searchFormWrapper.querySelector('.search-results');
					if (searchResultBoxes != undefined) {
						searchResultBoxes.classList.remove('open');
					}
				}
				event.preventDefault();
				event.stopPropagation();
			});
		});

		const solrForms = document.querySelectorAll('.tx-solr-search-form-pi-results');
		if (solrForms.length > 0) {
			solrForms.forEach((solrForm) => {
				const searchInput = <HTMLInputElement>solrForm.querySelector('.js-solr-q');

				// 2. Add an event listener for the 'submit' event
				solrForm.addEventListener('submit', function (event) {
					event.preventDefault();

					// 4. Get the search term and make sure it's not empty
					const searchTerm = searchInput?.value?.trim();

					if (searchTerm) {
						// 5. Get the base URL from the form's action attribute
						// This makes the script more robust, e.g. /suche/
						const baseUrl = this.getAttribute('action');

						// 6. Construct the new pretty URL and redirect the user
						// encodeURIComponent handles special characters like spaces or '&'
						window.location.href = baseUrl + encodeURIComponent(searchTerm);
					}
				});
			});
		}
	};

	/**
	 * Send the request and handle its result
	 * @param searchField The user input field
	 */
	private send = (searchField: HTMLInputElement, isHeader: boolean) => {
		// If the controller has been set, it's because a request is already running. Abort it
		if (this.controller != null) {
			this.controller.abort();
		}

		const searchText = searchField.value;

		// We know for a fact at this point that this is a fresh request, set the controller
		this.controller = new AbortController();

		const params = new URLSearchParams();
		params.append('q', searchText);

		fetch(`/inline-search?${params}`, {
			method: 'POST',
			signal: this.controller.signal,
		})
			.then((response): Promise<InlineSearchResult> => {
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				// Parse JSON response
				return response.json();
			})
			.then((data) => {
				// Request finished successfully - reset the controller
				this.controller = null;
				const searchResultBoxes = searchField
					.closest('.tx-solr-search-form')
					?.querySelectorAll('.search-results');
				if (searchResultBoxes != undefined) {
					searchResultBoxes.forEach((box) => {
						box.querySelectorAll('.resultset').forEach((resultset) => {
							this.renderItems(data.result, searchText, resultset);
						});
					});

					if (isHeader) this.setMenuHeight(true);
					else {
						if (data.result.length > 0) {
							searchResultBoxes.forEach((box) => box.classList.add('open'));
						} else {
							searchResultBoxes.forEach((box) => box.classList.remove('open'));
						}
					}
				}
			})
			.catch((err: Error) => {
				// Error thrown because we cancelled it ourselves - this is fine, ignore
				if (err.name === 'AbortError') return;

				console.error(err);
			});
	};

	/**
	 * Render the search results
	 * @param items Items from result
	 * @param searchText The text the user searched for. Used to make results bold
	 */
	private renderItems = (items: InlineSearchItem[], searchText: string, resultSetBox: Element) => {
		resultSetBox.textContent = '';

		items.forEach((item) => {
			const wrapper = document.createElement('a');
			wrapper.classList.add('result');
			wrapper.href = item.url;

			const title = document.createElement('span');
			const crumb = document.createElement('span');
			crumb.classList.add('crumb');
			// Breadcrumb is server-rendered HTML from our Breadcrumb partial (not user input)
			crumb.innerHTML = item.breadcrumb ?? '';
			title.innerHTML = highlightMatch(item.title, searchText);

			wrapper.appendChild(title);
			wrapper.appendChild(crumb);

			resultSetBox.appendChild(wrapper);
		});
	};

	/**
	 * Sets the search menu height
	 * @param menuOpen Whether the menu should be open or not
	 */
	private setMenuHeight(menuOpen: boolean) {
		const searchContainer = document.querySelector<HTMLDivElement>('.search-container');

		if (searchContainer == null) return;

		let submenuHeight;

		if (menuOpen === true) {
			// Calculate the height of the submenu
			submenuHeight = searchContainer.children[0].scrollHeight + 'px';

			searchContainer.classList.add('open');
		} else {
			submenuHeight = '0px';
			searchContainer.classList.remove('open');
		}

		searchContainer.style.height = submenuHeight;
	}

	private handleInputFocus() {
		const solrForms = document.querySelectorAll('.tx-solr-search-form-pi-results');

		solrForms.forEach((form) => {
			const searchField = form.querySelector<HTMLInputElement>('.input-group input.tx-solr-q');
			if (searchField === null) return;

			searchField.addEventListener('focus', (e) => {
				form.classList.add('focus');
			});
			searchField.addEventListener('blur', (e) => {
				form.classList.remove('focus');
			});
		});
	}
}
